import { CheckCircle2, FileText, History } from 'lucide-react'
import type {
  ChargebackDetailDecisionForm,
  ChargebackDetailInvestigationForm,
  ChargebackDetailOption,
} from '../types/pages'

export const CHARGEBACK_TABS = [
  { id: 'investigation', label: 'Investigation', icon: FileText, testId: 'chargeback-detail-tab-investigation' },
  { id: 'decision', label: 'Decision', icon: CheckCircle2, testId: 'chargeback-detail-tab-decision' },
  { id: 'history', label: 'Chargeback History', icon: History, testId: 'chargeback-detail-tab-history' },
] as const

export const CHARGEBACK_PAYMENT_TYPES = [
  'CONTRACT_CHARGEBACK',
  'CHGBACK_APP_FEE',
  'CHGBACK_CONTRACT',
]

export const CHARGEBACK_DETAIL_LABEL_CLASS =
  'text-xs font-bold uppercase tracking-wider text-slate-700'
export const CHARGEBACK_DETAIL_VALUE_CLASS =
  'text-sm font-normal text-slate-600'
export const CHARGEBACK_DETAIL_INPUT_CLASS =
  'w-full h-10 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-[#1597ff] focus:outline-none focus:ring-1 focus:ring-[#1597ff] disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 transition-colors'
export const CHARGEBACK_DETAIL_TEXTAREA_CLASS =
  'w-full min-h-[5.5rem] resize-none rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-800 placeholder:text-slate-400 transition-colors focus:border-[#1597ff] focus:outline-none focus:ring-1 focus:ring-[#1597ff]'
export const CHARGEBACK_DETAIL_PRIMARY_BUTTON_CLASS =
  'inline-flex h-9 items-center justify-center rounded-lg bg-[#1597ff] px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:opacity-50'
export const CHARGEBACK_DETAIL_SECONDARY_BUTTON_CLASS =
  'inline-flex h-9 items-center justify-center rounded-lg border border-[#1597ff] bg-white px-4 text-sm font-semibold text-[#1597ff] shadow-sm transition-all hover:bg-blue-50/50 disabled:cursor-not-allowed disabled:opacity-50'
export const CHARGEBACK_DETAIL_CARD_CLASS =
  'rounded-xl border border-slate-200 bg-white shadow-sm'
export const CHARGEBACK_DETAIL_TABLE_HEAD_CLASS =
  'border-b border-slate-100 bg-slate-50/70 text-[11px] font-bold uppercase tracking-wider text-slate-500'
export const CHARGEBACK_DETAIL_TABLE_TH_CLASS =
  'px-6 py-3.5'
export const CHARGEBACK_DETAIL_TABLE_TD_CLASS =
  'px-6 py-4'
export const CHARGEBACK_DETAIL_CHECKBOX_CARD_CLASS =
  'flex cursor-pointer items-center gap-3 select-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50/50'

export const EMPTY_INVESTIGATION_FORM: ChargebackDetailInvestigationForm = {
  chargeback_status: '',
  adjustment_reason: '',
  case_number: '',
  transaction_id: '',
  chargeback_amount: '',
  response_deadline: '',
  transaction_date: '',
  chargeback_notification_date: '',
  card_merchant: '',
  card_brand: '',
  card_no: '',
  spoke_resident: [],
  certificate_on_file: '',
  confirmed_move_in: '',
}

export const EMPTY_DECISION_FORM: ChargebackDetailDecisionForm = {
  chargeback_type: '',
  lost_reason: '',
  deposited_liberty: '',
  payment_status_updated: '',
  merchant_decision_date: '',
  funds_date: '',
  expire_application: false,
  cancelled_application: false,
  re_invoice: false,
  file_fraud: false,
  refunded: false,
}

export const CHARGEBACK_STATUS_OPTIONS: ChargebackDetailOption[] = [
  { value: 'INVESTIGATING', label: 'Investigating' },
  { value: 'CHALLENGED', label: 'Challenged' },
  { value: 'CHARGEBACK_COMPLETE', label: 'Chargeback Complete' },
]

export const CHARGEBACK_REASON_OPTIONS: ChargebackDetailOption[] = [
  { value: 'NOT_AUTHORIZED', label: 'Not Authorized' },
  { value: 'CONTESTING_CHARGES', label: 'Contesting Charges' },
  { value: 'BANK_HOLD', label: 'Bank Hold' },
  { value: 'OTHER', label: 'Other' },
]

export const CARD_MERCHANT_OPTIONS: ChargebackDetailOption[] = [
  { value: 'CARDCONNECT', label: 'CardConnect' },
  { value: 'PAYPAL', label: 'Paypal' },
]

export const CARD_BRAND_OPTIONS: ChargebackDetailOption[] = [
  { value: 'VISA', label: 'Visa' },
  { value: 'MASTERCARD', label: 'Mastercard' },
  { value: 'AMEX', label: 'Amex' },
  { value: 'DISCOVER', label: 'Discover' },
]

export const SPOKE_RESIDENT_OPTIONS: ChargebackDetailOption[] = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
  { value: 'LEFT_VOICEMAIL', label: 'Left Voicemail' },
  { value: 'SENT_EMAIL', label: 'Sent Email' },
  { value: 'SENT_TEXT', label: 'Sent Text' },
]

export const YES_NO_OPTIONS: ChargebackDetailOption[] = [
  { value: '1', label: 'Yes' },
  { value: '0', label: 'No' },
]

export const CHARGEBACK_RESULT_OPTIONS: ChargebackDetailOption[] = [
  { value: 'WON_CREDIT', label: 'Won - Credit' },
  { value: 'LOST_DEBIT', label: 'Lost - Debit' },
  { value: 'CANCELLED_BANK', label: 'Cancelled - Bank' },
  { value: 'CANCELLED_APPLICANT', label: 'Cancelled - Applicant' },
  { value: 'ACCEPTED_CHARGEBACK', label: 'Accepted Chargeback' },
  { value: 'CLOSE_CASE', label: 'Close Case - No issuer response' },
  { value: 'EXPIRED', label: 'Expired' },
]

export const LOST_REASON_OPTIONS: ChargebackDetailOption[] = [
  { value: 'DENIED', label: 'Denied' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'ACCEPTED_CHARGEBACK', label: 'Accepted Chargeback' },
]

/** Angular FileTypeSelection: picking this swaps `check_types` for the free-text value. */
export const CHARGEBACK_FILE_TYPE_OTHER = 'OTHER'

export const CHARGEBACK_FILE_MAX_SIZE_MB = 5

export const INVESTIGATION_FILE_TYPES: Record<string, string> = {
  LEASE: 'Lease',
  TRANSACTION_DETAILS: 'Transaction Details',
  DISPUTE_NOTIFICATION: 'Dispute Notification',
  CARDPOINTE_RECEIPT: 'CardPointe Receipt',
  CHARGEBACK_SUMMARY: 'Chargeback Summary',
  OTHER: 'Other',
}

export const DECISION_FILE_TYPES: Record<string, string> = {
  MERCHANT_OUTPUT_PACKAGE: 'Merchant Output Package',
  CARDPOINTE_RECEIPT: 'CardPointe Receipt',
  PAYPAL_RECEIPT: 'Paypal Receipt',
  CORNERSTONE_RECEIPT: 'Cornerstone receipt',
  SUBMITTED_CHARGEBACK_PACKAGE: 'Submitted Chargeback Package',
  MERCHANT_OUTPUT_PACKAGE_DENIED: 'Merchant Output Package - Denied',
  OTHER: 'Other',
}

export const ALL_DECISION_FILE_TYPES: Record<string, string> = {
  ...INVESTIGATION_FILE_TYPES,
  ...DECISION_FILE_TYPES,
}
